/**
 * Determines whether all the action buttons for the VideoDetails page have the necessary data
 */
import {
  useAppProperties,
  useFavourites,
  useMainTabs,
  usePlaylists,
  useUserEvents,
  useWatchlist,
} from "@/hooks";
import { useComments } from "@/app/modules/Comments/hooks/useComments.tsx";
import { ItemType } from "@/api";

interface ActionsLoading {
  itemId?: number;
  itemType: ItemType;
  sourceId?: string;
  dataSource?: string;
  liveEventStartDate?: string;
  liveEventEnded?: string;
}

export const useActionsLoading = ({
  itemId,
  itemType,
  sourceId,
  dataSource,
  liveEventStartDate,
  liveEventEnded,
}: ActionsLoading) => {
  const { showCustomPlaylists, showCalendar, showFavourites, showWatchlist } =
    useAppProperties();
  // Required for available buttons
  const { data: mainTabs } = useMainTabs();
  const { data: properties } = useAppProperties();

  const commentsBelowPlayer =
    properties?.WebItemDetailsDesign === "CommentsBelowPlayer";

  // Required for save button
  const { data: playlists, isFetched: playlistsFetched } = usePlaylists();
  const { data: favourites, isFetched: favouritesFetched } = useFavourites();
  const { data: watchlist, isFetched: watchlistFetched } = useWatchlist();

  // Required for complete button
  const { data: events, isFetched: eventsFetched } = useUserEvents();

  // Required for comments button
  const {
    data: comments,
    isFetched: commentsFetched,
    disabled: commentsDisabled,
  } = useComments({
    dataSource: dataSource ?? "",
    sourceId: sourceId ?? "",
    itemId: itemId ?? 0,
    itemType,
    liveEventStartDate,
    liveEventEnded,
  });

  const done = [
    mainTabs,
    playlists || playlistsFetched || !showCustomPlaylists,
    favourites || favouritesFetched || !showFavourites,
    watchlist || watchlistFetched || !showWatchlist,
    events || eventsFetched || !showCalendar,
    commentsBelowPlayer || comments || commentsFetched || commentsDisabled,
  ].every(Boolean); // all should be truthy
  return { loading: !done };
};
